import React, { useState, useRef, useEffect } from 'react';
import { marked } from "marked";
import DOMPurify from "dompurify";

function App() {
  const [messages, setMessages] = useState(() => JSON.parse(localStorage.getItem("chatHistory")) || []);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
    localStorage.setItem("chatHistory", JSON.stringify(messages));
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    const response = await fetch("http://localhost:8000/chat", {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: input })
    });

    const data = await response.json();
    const botMessage = {
      role: "bot",
      content: data.response,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, botMessage]);
    setIsTyping(false);
  };

  const ChatMessage = ({ content }) => {
    if (typeof content !== "string") return null;

    const cleanHTML = DOMPurify.sanitize(marked.parse(content));

    return (
        <div
            className="bg-white p-3 rounded-xl shadow text-sm leading-relaxed prose"
            dangerouslySetInnerHTML={{ __html: cleanHTML }}
        />
    );
  };

  return (
    <div className="flex justify-center items-center h-screen bg-gray-100 p-4">
      <div className="w-[360px] h-[600px] bg-white shadow-lg rounded-2xl flex flex-col overflow-hidden">

        <div className="flex items-center bg-green-600 text-white px-4 py-3 gap-3">
          <img
            src="https://ui-avatars.com/api/?name=Agent&background=075e54&color=fff"
            alt="Agent"
            className="w-10 h-10 rounded-full"
          />
          <div>
            <h4 className="text-sm font-semibold">Support Agent</h4>
            <span className="text-xs text-white/80">Typically replies in a few minutes</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#ece5dd] px-4 py-3 space-y-3 scrollbar-thin">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`flex items-end gap-2 max-w-[75%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                <img
                  src={
                    msg.role === "user"
                      ? "https://ui-avatars.com/api/?name=You&background=34b7f1&color=fff"
                      : "https://ui-avatars.com/api/?name=Bot&background=888&color=fff"
                  }
                  alt={msg.role}
                  className="w-8 h-8 rounded-full"
                />
                <div
                  className={`px-4 py-2 text-sm rounded-xl shadow ${
                    msg.role === "user"
                      ? "bg-[#dcf8c6] rounded-br-none"
                      : "bg-white rounded-bl-none"
                  }`}
                >
                  <ChatMessage content={msg.content} />
                  <div className="text-[10px] text-gray-500 text-right mt-1">{msg.timestamp}</div>
                </div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="text-xs text-gray-500 italic animate-pulse">Bot is typing...</div>
          )}
          <div ref={messagesEndRef}></div>
        </div>

        <div className="flex items-center bg-[#f0f0f0] px-3 py-2 border-t">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            type="text"
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 rounded-full bg-white border text-sm focus:outline-none"
          />
          <button
            onClick={sendMessage}
            className="ml-2 p-2 bg-green-600 hover:bg-green-700 text-white rounded-full text-lg"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
